import { useState, useEffect } from 'react';
import {
  ApolloProvider,
  ApolloClient,
  HttpLink,
  ApolloLink,
  InMemoryCache,
  concat,
  from,
  Observable,
} from '@apollo/client';
import { CachePersistor, LocalStorageWrapper } from 'apollo3-cache-persist';
import removeAuthCredentials from '../Auth/removeAuthCredentials';
// import { reportError } from '@dietlabs/utils';
import { onError } from '@apollo/client/link/error';
import Loader from '../Layout/Loader';

// Custom link to handle file uploads with multipart/form-data
const createUploadLink = (options) => {
  const httpLink = new HttpLink(options);

  return new ApolloLink((operation, forward) => {
    // Check if the operation contains files
    const files = [];
    const map = {};
    let fileIndex = 0;

    const extractFiles = (obj, path = '') => {
      if (obj instanceof File) {
        const fileKey = `${fileIndex}`;
        files.push(obj);
        map[fileKey] = [path];
        fileIndex++;
        return null;
      }

      if (Array.isArray(obj)) {
        return obj.map((item, index) => extractFiles(item, `${path}.${index}`));
      }

      if (obj && typeof obj === 'object') {
        const result = {};
        for (const key in obj) {
          const newPath = path ? `${path}.${key}` : `variables.${key}`;
          result[key] = extractFiles(obj[key], newPath);
        }
        return result;
      }

      return obj;
    };

    const variables = extractFiles(operation.variables);

    if (files.length > 0) {
      // Create FormData for multipart upload
      const formData = new FormData();

      const operations = {
        query: operation.query.loc.source.body,
        variables,
        operationName: operation.operationName,
      };

      formData.append('operations', JSON.stringify(operations));
      formData.append('map', JSON.stringify(map));

      files.forEach((file, index) => {
        formData.append(index.toString(), file);
      });

      // Custom fetch for file upload
      const uri = operation.getContext().uri || options.uri;
      const headers = operation.getContext().headers || {};
      const fetchOptions = operation.getContext().fetchOptions || {};

      return new Observable((observer) => {
        const abortController = new AbortController();
        const signal = fetchOptions.signal || abortController.signal;

        // If external signal is provided, listen to it and abort our controller
        if (fetchOptions.signal) {
          fetchOptions.signal.addEventListener('abort', () => {
            abortController.abort();
          });
        }

        fetch(uri, {
          method: 'POST',
          body: formData,
          credentials: options.credentials || 'same-origin',
          signal: signal,
          headers: {
            ...headers,
            // Don't set Content-Type, browser will set it with boundary
          },
        })
          .then((response) => response.json())
          .then((result) => {
            observer.next(result);
            observer.complete();
          })
          .catch((error) => {
            observer.error(error);
          });

        // Return cleanup function for Observable
        return () => {
          abortController.abort();
        };
      });
    }

    // No files, use standard HTTP link
    return httpLink.request(operation, forward);
  });
};

const httpLink = createUploadLink({
  uri: import.meta.env.VITE_REACT_APP_GRAPHQL_URL,
  credentials: 'same-origin',
  headers: {
    'X-AppEnvironment': import.meta.env.VITE_REACT_APP_ENV,
    'X-AppVersion': `ReactWebApp/${process.env.VITE_APP_VERSION}`,
  },
});

// eslint-disable-next-line import/no-mutable-exports
export let networkErrorVar;

const errorLink = onError(({ networkError, graphQLErrors }) => {
  if (graphQLErrors) {
    graphQLErrors.map(
      ({ message, locations, path }) =>
        console.log(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`)
      // reportError(
      //     `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
      // )
    );
  }
  if (networkError) {
    const newError = new Error('Network error', 'Failed to fetch');
    newError.networkError = networkError;
    networkErrorVar = newError;

    const statusCode = networkError.statusCode ?? networkError.response?.status;

    // Unauthorized
    if (statusCode === 401) {
      removeAuthCredentials();
      window.location.reload();
    }
  }
});

const authMiddleware = new ApolloLink((operation, forward) => {
  // add the authorization to the headers and set dynamic api uri
  operation.setContext(({ headers = {} }) => ({
    headers: {
      ...headers,
      'X-Authentication': headers['X-Authentication'] || localStorage?.getItem('token') || null,
      'X-Locale': localStorage?.getItem('i18nextLng')?.replace('-', '_') || 'en_US',
      'X-RequestClientTime': new Date().toISOString(),
      'X-TimeZone': Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
    uri:
      localStorage.getItem('developerOptionsApiUri') ||
      `${import.meta.env.VITE_REACT_APP_GRAPHQL_URL}?${operation.operationName}`,
  }));

  return forward(operation);
});

const appLink = from([errorLink, concat(authMiddleware, httpLink)]);

const Apollo = ({ children }) => {
  const [client, setClient] = useState(null);

  useEffect(() => {
    async function init() {
      const cache = new InMemoryCache({
        typePolicies: {
          UserShoppingListCategoryProduct: {
            keyFields: ['key'],
          },
          UserHydrationDay: {
            keyFields: ['key'],
          },
          UserDiet: {
            keyFields: ['id'],
          },
          UserDailyDietPlanType: {
            keyFields: ['id'],
          },
          UserDietSet: {
            keyFields: ['dietSetId'],
          },
          UserDailyDietPlanEventMeal: {
            keyFields: ['key'],
          },
          UserDailyDietMeal: {
            keyFields: ['key'],
          },
          UserActivity: {
            keyFields: ['id'],
          },
          UserDailyDietMealDishIngredient: {
            keyFields: ['key'],
          },
          UserMeasurement: {
            keyFields: [],
          },
          Diabetes: {
            keyFields: [],
          },
          BloodSugarMeasurementQuery: {
            keyFields: [],
          },
          Treatment: {
            keyFields: [],
          },
          Cookbook: {
            keyFields: [],
          },
          CookbookIngredient: {
            keyFields: ['key'],
          },
          DietDishIngredient: {
            keyFields: ['productId'],
          },
          DailyTrackerQuery: {
            keyFields: [],
          },
          DailyTrackerData: {
            keyFields: ['date'],
          },
          // The item id repeats across date ranges while isBought is per range — never normalize.
          DailyTrackerShoppingListItem: {
            keyFields: false,
          },
        },
      });

      let newPersistor = new CachePersistor({
        cache,
        storage: new LocalStorageWrapper(window.localStorage),
        debug: false,
        trigger: 'write',
        debounce: 0,
      });

      try {
        await newPersistor.restore();
      } catch (error) {
        console.error('Failed to restore Apollo cache:', error);
        localStorage.removeItem('apollo-cache-persist');
        window.location.reload();
        return;
      }

      setClient(
        new ApolloClient({
          cache,
          link: appLink,
        })
      );
    }

    init().catch(console.error);
  }, []);

  if (!client) {
    return (
      <div className="full-height">
        <Loader />
      </div>
    );
  }

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};

export default Apollo;
